import { useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { User } from 'firebase/auth';
import {
  signInWithGoogle,
  signOut as firebaseSignOut,
  subscribeToAuthState,
} from '../services/auth.service';
import { getOrCreateAppUser } from '../services/appUser.service';
import { AuthContext } from './auth-context';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  // Start true — Firebase needs a moment to restore session from local storage.
  // This prevents a flash of the login page on refresh when the user is already signed in.
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeToAuthState((firebaseUser) => {
      setUser(firebaseUser);
      setIsLoading(false);
    });
    return unsubscribe;
  }, []);

  const signIn = useCallback(async () => {
    setError(null);
    try {
      const user = await signInWithGoogle();
      // Create appUsers doc on first sign-in; update lastSignIn on subsequent sign-ins.
      await getOrCreateAppUser(user.uid, user.displayName ?? '', user.email ?? '');
    } catch (err) {
      const authErr = err as { message?: string };
      setError(authErr?.message ?? 'Sign-in failed. Please try again.');
    }
  }, []);

  const signOut = useCallback(async () => {
    setError(null);
    try {
      await firebaseSignOut();
    } catch (err) {
      const authErr = err as { message?: string };
      setError(authErr?.message ?? 'Sign-out failed. Please try again.');
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return (
    <AuthContext.Provider value={{ user, isLoading, error, signIn, signOut, clearError }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
